/**
 * One person, many runs: every run is a membership in a cohort (a solo run is a cohort too). Which one the person is taken to is
 * remembered per device and per person; with several and none remembered the landing is the list ("Your runs", `/runs`).
 */
import type { Membership, Principal } from "@bsim/contracts";
import { useRoute } from "../api/context";
import type { MenuItem } from "../theme/Menu";
import { navigate } from "./history";

export const RUNS_PATH = "/runs";
export const welcomedKey = (cohortId: string): string => `bsim.welcomed.${cohortId}`;
const lastRunKey = (userId: string): string => `bsim.lastRun.${userId}`;

/** The run this person opened last on this device, or null (no storage, or never). */
export function lastRun(userId: string): string | null {
  try { return localStorage.getItem(lastRunKey(userId)); } catch { return null; }
}
export function rememberRun(userId: string, cohortId: string): void {
  try { localStorage.setItem(lastRunKey(userId), cohortId); } catch { /* the list is then the landing next time */ }
}

/** Where one run opens: a facilitator on the Board, a solo player on the solo page (for that run when `named`), otherwise Your Bank on a first visit and the cockpit after. */
export function runPath(m: Membership, named = false): string {
  if (m.role === "facilitator") return `/f/${m.cohortId}/board`;
  if (m.cohortMode === "solo") return named ? `/solo?run=${m.cohortId}` : "/solo";
  let welcomed = false;
  try { welcomed = localStorage.getItem(welcomedKey(m.cohortId)) === "1"; } catch { /* no storage: treat as a first visit */ }
  return `/c/${m.cohortId}/${welcomed ? "cockpit" : "bank"}`;
}

/** The run to open on arrival: the only one; else the last one opened on this device; else null (show the list). */
export function chosenRun(principal: Principal): Membership | null {
  if (principal.memberships.length <= 1) return principal.memberships[0] ?? null;
  const last = lastRun(principal.userId);
  return principal.memberships.find((m) => m.cohortId === last) ?? null;
}

/** "Switch run" for the account menu (and the phone menu sheet): only when there is another run to switch to. */
export function useAccountItems(): readonly MenuItem[] {
  const me = useRoute("authMe", undefined, { staleTime: Infinity });
  return (me.data?.principal.memberships.length ?? 0) > 1 ? [{ key: "switch-run", label: "Switch run", onSelect: () => navigate(RUNS_PATH) }] : [];
}
